import { STYLE_MARKERS, CSS_RULES, RESET_ICON_SVG } from "./constants";

/** Clears Dialogflow Messenger storage so the next load starts a new session. Avoids permission issues from session APIs. */
export const clearMessengerStorage = () => {
  try {
    const match = (k) => k && (k === "df-messenger-storage" || k.includes("df-messenger"));
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (match(k)) sessionStorage.removeItem(k);
    }
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (match(k)) localStorage.removeItem(k);
    }
  } catch (_) {}
};

export const injectStyle = (shadowRoot, marker, cssRules) => {
  if (!shadowRoot || shadowRoot.querySelector(`style[${marker}], style#${marker}`)) {
    return;
  }
  const style = document.createElement("style");
  if (marker.startsWith("data-")) {
    style.setAttribute(marker, "true");
  } else {
    style.id = marker;
  }
  style.textContent = cssRules;
  shadowRoot.appendChild(style);
};

/**
 * Injects the welcome message (disclaimer + greeting) into the Dialogflow message list shadow root.
 * @param {ShadowRoot} messageListShadow - Shadow root of df-messenger-message-list
 * @param {string} disclaimerMessage - Disclaimer text (required, use default from config if not provided by caller)
 * @param {string} welcomeGreeting - Welcome greeting text (required, use default from config if not provided by caller)
 * @returns {boolean} True if injection succeeded
 */
export const injectWelcomeMessage = (messageListShadow, disclaimerMessage, welcomeGreeting) => {
  if (!messageListShadow) {
    return false;
  }

  const disclaimer = typeof disclaimerMessage === "string" ? disclaimerMessage : "";
  const greeting = typeof welcomeGreeting === "string" ? welcomeGreeting : "";

  const messageContainer =
    messageListShadow.querySelector("#message-list .content") ||
    messageListShadow.querySelector(".content") ||
    messageListShadow.querySelector("#message-list");

  if (!messageContainer) {
    return false;
  }

  const existingWelcome = messageContainer.querySelector(".stewart-welcome-message");
  if (existingWelcome) {
    existingWelcome.remove();
  }

  injectStyle(messageListShadow, STYLE_MARKERS.WELCOME_OVERRIDE, CSS_RULES.WELCOME_OVERRIDE);

  const welcomeEntry = document.createElement("div");
  welcomeEntry.className = "entry bot stewart-welcome-message";
  welcomeEntry.style.cssText = "margin-top: 0 !important; margin-block-start: 0 !important;";

  const messageStack = document.createElement("div");
  messageStack.className = "message-stack";

  if (disclaimer) {
    const disclaimerWrapper = document.createElement("div");
    disclaimerWrapper.className = "df-text-message first-child";

    const disclaimerDiv = document.createElement("div");
    disclaimerDiv.className = "message bot-message";

    const disclaimerContent = document.createElement("div");
    disclaimerContent.style.cssText = "white-space: pre-wrap;";
    disclaimerContent.textContent = disclaimer;

    disclaimerDiv.appendChild(disclaimerContent);
    disclaimerWrapper.appendChild(disclaimerDiv);
    messageStack.appendChild(disclaimerWrapper);
  }

  if (greeting) {
    const greetingWrapper = document.createElement("div");
    greetingWrapper.className = "df-text-message last-child";

    const greetingDiv = document.createElement("div");
    greetingDiv.className = "message bot-message";

    const greetingContent = document.createElement("div");
    greetingContent.style.cssText = "white-space: pre-wrap;";
    greetingContent.textContent = greeting;

    greetingDiv.appendChild(greetingContent);
    greetingWrapper.appendChild(greetingDiv);
    messageStack.appendChild(greetingWrapper);
  }

  if (messageStack.childNodes.length === 0) {
    return false;
  }

  welcomeEntry.appendChild(messageStack);
  messageContainer.insertBefore(welcomeEntry, messageContainer.firstChild);

  return true;
};

export const applyStyles = (bubble) => {
  if (!bubble?.shadowRoot) return false;

  const bubbleShadowRoot = bubble.shadowRoot;
  injectStyle(bubbleShadowRoot, STYLE_MARKERS.CHAT_WRAPPER, CSS_RULES.CHAT_WRAPPER);

  const chatElement = bubbleShadowRoot.querySelector("df-messenger-chat");
  if (!chatElement?.shadowRoot) return false;

  const chatShadowRoot = chatElement.shadowRoot;
  injectStyle(chatShadowRoot, STYLE_MARKERS.MESSAGE_LIST, CSS_RULES.MESSAGE_LIST);

  const messageListElement = chatShadowRoot.querySelector("df-messenger-message-list");
  if (!messageListElement?.shadowRoot) return false;

  const messageListShadow = messageListElement.shadowRoot;
  injectStyle(messageListShadow, STYLE_MARKERS.NESTED_MESSAGE_LIST, CSS_RULES.MESSAGE_LIST);

  return true;
};

export const watchForMessageContainer = (
  messageListShadow,
  welcomeInjectedRef,
  observersRef,
  disclaimerMessage,
  welcomeGreeting
) => {
  if (!messageListShadow || welcomeInjectedRef.current) return;

  let observer = null;
  let intervalId = null;
  let timeoutId = null;

  const tryInject = () => {
    if (welcomeInjectedRef.current) return false;

    const messageContainer =
      messageListShadow.querySelector("#message-list .content") ||
      messageListShadow.querySelector(".content") ||
      messageListShadow.querySelector("#message-list");

    if (messageContainer) {
      const success = injectWelcomeMessage(
        messageListShadow,
        disclaimerMessage,
        welcomeGreeting
      );
      if (success) {
        welcomeInjectedRef.current = true;
        
        if (observer) {
          observer.disconnect();
          observer = null;
        }
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        
        return true;
      }
    }
    return false;
  };

  if (tryInject()) return;

  observer = new MutationObserver(() => {
    tryInject();
  });
  
  observer.observe(messageListShadow, { 
    childList: true, 
    subtree: true,
    attributes: false,
    characterData: false
  });
  observersRef.current.push(observer);

  intervalId = setInterval(() => {
    if (tryInject()) {
      clearInterval(intervalId);
    }
  }, 100);

  timeoutId = setTimeout(() => {
    if (observer) observer.disconnect();
    if (intervalId) clearInterval(intervalId);
  }, 15000);
};

export const observeShadowRoot = (element, callback, observersRef) => {
  if (!element) return null;

  const observer = new MutationObserver(() => {
    if (element.shadowRoot) {
      callback(element.shadowRoot);
      observer.disconnect();
    }
  });

  if (element.shadowRoot) {
    callback(element.shadowRoot);
  } else {
    observer.observe(element, { childList: true, subtree: true });
    observersRef.current.push(observer);
  }

  return observer;
};

/**
 * Returns the message-list shadow root from a df-messenger element, or null.
 */
export const getMessageListShadow = (messenger) => {
  if (!messenger) return null;
  const bubble = messenger.querySelector("df-messenger-chat-bubble");
  if (!bubble?.shadowRoot) return null;
  const chat = bubble.shadowRoot.querySelector("df-messenger-chat");
  if (!chat?.shadowRoot) return null;
  const list = chat.shadowRoot.querySelector("df-messenger-message-list");
  return list?.shadowRoot ?? null;
};

/**
 * Re-injects welcome message into messenger (e.g. after startNewSession).
 * Uses injectWelcomeMessage and watchForMessageContainer; retries until container is ready.
 */
export const reinjectWelcome = (
  messenger,
  disclaimerMessage,
  welcomeGreeting,
  welcomeInjectedRef,
  observersRef
) => {
  if (!messenger) return;
  welcomeInjectedRef.current = false;

  const messageListShadow = getMessageListShadow(messenger);
  if (messageListShadow) {
    if (injectWelcomeMessage(messageListShadow, disclaimerMessage, welcomeGreeting)) {
      welcomeInjectedRef.current = true;
    } else {
      watchForMessageContainer(
        messageListShadow,
        welcomeInjectedRef,
        observersRef,
        disclaimerMessage,
        welcomeGreeting
      );
    }
  }

  let attempts = 0;
  const id = setInterval(() => {
    if (welcomeInjectedRef.current || ++attempts > 25) {
      clearInterval(id);
      return;
    }
    const shadow = getMessageListShadow(messenger);
    if (shadow && injectWelcomeMessage(shadow, disclaimerMessage, welcomeGreeting)) {
      welcomeInjectedRef.current = true;
      clearInterval(id);
    }
  }, 150);
};

/**
 * Finds the slotted df-messenger-titlebar element (light DOM child of df-messenger-chat).
 * @param {HTMLElement} messenger - The df-messenger element
 * @returns {HTMLElement|null}
 */
export const findSlottedTitlebar = (messenger) => {
  if (!messenger) return null;
  const bubble = messenger.querySelector("df-messenger-chat-bubble");
  if (!bubble?.shadowRoot) return null;
  const chat = bubble.shadowRoot.querySelector("df-messenger-chat");
  if (!chat) return null;
  return (
    chat.querySelector('df-messenger-titlebar[slot="titlebar"]') ||
    chat.querySelector("df-messenger-titlebar")
  );
};

/**
 * Injects the reset (new conversation) button into the df-messenger-titlebar as a
 * slotted action button (sibling to the close/minimize button, NOT nested inside it).
 * Also adds "Close" tooltip to the existing close/Close button.
 * @param {HTMLElement} messenger - The df-messenger element
 * @param {Function} onReset - Callback when the button is clicked
 */
export const injectResetButton = (messenger, onReset) => {
  const titlebar = findSlottedTitlebar(messenger);
  if (!titlebar) return false;

  if (titlebar.querySelector(".stewart-reset-btn")) return true;

  const closeBtn =
    titlebar.querySelector("button.close-action") ||
    titlebar.querySelector('button[slot="action"]');

  if (closeBtn && !closeBtn.title) {
    closeBtn.title = "Close";
    closeBtn.setAttribute("aria-label", "Close chat");
  }

  injectStyle(titlebar, STYLE_MARKERS.RESET_BUTTON, CSS_RULES.RESET_BUTTON);

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "stewart-reset-btn";
  btn.setAttribute("slot", "action");
  btn.setAttribute("aria-label", "Start new conversation");
  btn.title = "New conversation";
  btn.disabled = true;
  btn.setAttribute("aria-disabled", "true");
  btn.innerHTML = RESET_ICON_SVG;
  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    if (!btn.disabled) onReset();
  });

  if (closeBtn) {
    titlebar.insertBefore(btn, closeBtn);
  } else {
    titlebar.appendChild(btn);
  }
  return true;
};

// Matches "projects/{p}/agents/{a}/sessions/{s}" out of the Dialogflow Messenger
// webhook URL, e.g. https://dialogflow.cloud.google.com/v1/cx/integrations/messenger/webhook/projects/.../agents/.../sessions/...
const DF_SESSION_URL_REGEX = /(projects\/[^/]+\/agents\/[^/]+\/sessions\/[^/?#]+)/;

/**
 * Extracts "projects/{p}/agents/{a}/sessions/{s}" from a Dialogflow Messenger webhook URL.
 * @param {string} url
 * @returns {string|null}
 */
export const extractConversationName = (url) => {
  if (!url || typeof url !== "string") return null;
  const match = url.match(DF_SESSION_URL_REGEX);
  return match ? match[1] : null;
};

const dfConversationListeners = new Set();
let dfConversationCapturePatched = false;

const installDfConversationCapture = () => {
  if (dfConversationCapturePatched) return;
  dfConversationCapturePatched = true;

  const notify = (url) => {
    const conversationName = extractConversationName(url);
    if (conversationName) {
      dfConversationListeners.forEach((listener) => listener(conversationName));
    }
  };

  const originalFetch = window.fetch;
  if (typeof originalFetch === "function") {
    window.fetch = function (input, init) {
      notify(typeof input === "string" ? input : input?.url);
      return originalFetch.call(this, input, init);
    };
  }

  const originalOpen = window.XMLHttpRequest?.prototype.open;
  if (typeof originalOpen === "function") {
    window.XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      notify(url);
      return originalOpen.call(this, method, url, ...rest);
    };
  }
};

/**
 * Registers a listener that fires with "projects/.../agents/.../sessions/..." whenever
 * df-messenger sends a request to the Dialogflow webhook. The session path is only
 * present in the request URL, not in any JS event payload, so fetch/XHR are patched
 * (once, globally) to observe it.
 * @param {(conversationName: string) => void} onCapture
 * @returns {() => void} unregister function
 */
export const captureDialogflowConversationName = (onCapture) => {
  installDfConversationCapture();
  dfConversationListeners.add(onCapture);
  return () => dfConversationListeners.delete(onCapture);
};

